import { useTranslations } from 'next-intl';
import { useEffect, useRef } from 'react';
import { toast } from '@/app/components/ui/use-toast';
import { useStore, useStoreApi } from '@/app/providers/store';
import type { ImagePrompt, ImageUploadFile, ReferencePrompt } from '@/app/types';
import {
  extractTempImageData,
  registerTempUploadRetryHandler,
  type TempImageUploadOutcome,
  type TempUploadReplacement,
  uploadFile,
  uploadTempImages,
} from '@/app/utils/client';

type UploadItem = { id: string; status: 'done' | 'uploading'; prompt: ImagePrompt | null };
type UploadRefs = { processed: Set<string>; pending: Set<string>; failed: Set<string> };

const getPromptReplacements = (before: ImagePrompt, after: ImagePrompt): TempUploadReplacement[] => {
  return [
    { from: before.sourceImage, to: after.sourceImage },
    { from: before.selectedImage, to: after.selectedImage },
  ].filter(({ from, to }) => !!from && !!to && from !== to);
};

const scaleSelectedImageRange = (
  range: ImagePrompt['selectedImageRange'],
  prompt: ImagePrompt,
  width: number,
  height: number,
): ImagePrompt['selectedImageRange'] => {
  if (!range || !prompt.sourceImageWidth || !prompt.sourceImageHeight) return range;
  const scaleX = width / prompt.sourceImageWidth;
  const scaleY = height / prompt.sourceImageHeight;
  return [
    Math.round(range[0] * scaleX),
    Math.round(range[1] * scaleY),
    Math.round(range[2] * scaleX),
    Math.round(range[3] * scaleY),
  ];
};

const buildUploadedPrompt = (
  prompt: ImagePrompt,
  result: Extract<TempImageUploadOutcome, { ok: true }>,
): ImagePrompt => {
  const selectedWidth = prompt._tempSelectedImage
    ? (prompt.selectedImageWidth ?? prompt.sourceImageWidth ?? 0)
    : (prompt._tempSourceFallback?.width ?? prompt.selectedImageWidth ?? prompt.sourceImageWidth ?? 0);
  const selectedHeight = prompt._tempSelectedImage
    ? (prompt.selectedImageHeight ?? prompt.sourceImageHeight ?? 0)
    : (prompt._tempSourceFallback?.height ?? prompt.selectedImageHeight ?? prompt.sourceImageHeight ?? 0);
  const sourceWidth = result.sourceFromSelected
    ? selectedWidth
    : result.sourceDegraded && prompt._tempSourceFallback
      ? prompt._tempSourceFallback.width
      : (prompt.sourceImageWidth ?? 0);
  const sourceHeight = result.sourceFromSelected
    ? selectedHeight
    : result.sourceDegraded && prompt._tempSourceFallback
      ? prompt._tempSourceFallback.height
      : (prompt.sourceImageHeight ?? 0);
  const { _tempFile, _tempSelectedImage, _tempSourceFallback, _tempUploadError, ...rest } = prompt;
  const selectedImageRange = result.sourceFromSelected
    ? ([0, 0, sourceWidth, sourceHeight] as ImagePrompt['selectedImageRange'])
    : result.sourceDegraded
      ? scaleSelectedImageRange(prompt.selectedImageRange, prompt, sourceWidth, sourceHeight)
      : prompt.selectedImageRange;
  const sourceImagePreview = result.sourceFromSelected
    ? prompt.selectedImagePreview || prompt.sourceImagePreview
    : result.sourceDegraded && prompt._tempSourceFallback
      ? URL.createObjectURL(prompt._tempSourceFallback.blob)
      : prompt.sourceImagePreview;
  return {
    ...rest,
    sourceImage: result.sourceUri,
    sourceImagePreview,
    sourceImageWidth: sourceWidth,
    sourceImageHeight: sourceHeight,
    selectedImage: result.selectedUri ?? result.sourceUri,
    selectedImagePreview: result.selectedUri
      ? prompt.selectedImagePreview || prompt.sourceImagePreview
      : result.sourceUri,
    selectedImageWidth: result.selectedUri ? selectedWidth : sourceWidth,
    selectedImageHeight: result.selectedUri ? selectedHeight : sourceHeight,
    selectedImageRange,
  };
};

const useRunTempUploadBatch = <T extends UploadItem>({
  userInfo,
  items,
  keyPrefix,
  getLatestItem,
  updateItem,
}: {
  userInfo: unknown;
  items: T[];
  keyPrefix: string;
  getLatestItem: (id: string) => T | undefined;
  updateItem: (id: string, updater: (item: T, prompt: ImagePrompt) => T) => void;
}) => {
  const t = useTranslations();
  const refs = useRef<UploadRefs>({ processed: new Set(), pending: new Set(), failed: new Set() });
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const getLatestItemRef = useRef(getLatestItem);
  getLatestItemRef.current = getLatestItem;
  const updateItemRef = useRef(updateItem);
  updateItemRef.current = updateItem;

  const runItem = async (item: T): Promise<TempUploadReplacement[]> => {
    const currentItem = getLatestItemRef.current(item.id) ?? item;
    if (!currentItem.prompt) return [];
    updateItemRef.current(item.id, (latest) => ({ ...latest, status: 'uploading' }));
    const tempData = extractTempImageData(currentItem.prompt);
    if (tempData) {
      const result = (await uploadTempImages([tempData])).get(tempData.id);
      if (!result?.ok) {
        updateItemRef.current(item.id, (latest, prompt) => ({
          ...latest,
          status: 'done',
          prompt: { ...prompt, _tempUploadError: true },
        }));
        throw new Error(`temp image upload failed: ${result?.stage ?? 'unknown'}`);
      }
      const uploadedPrompt = buildUploadedPrompt(currentItem.prompt, result);
      updateItemRef.current(item.id, (latest) => ({
        ...latest,
        status: 'done',
        prompt: uploadedPrompt,
      }));
      return getPromptReplacements(currentItem.prompt, uploadedPrompt);
    }
    if (!currentItem.prompt._tempSelectedImage) return [];
    const previousPrompt = currentItem.prompt;
    const result = await uploadFile({
      file: currentItem.prompt._tempSelectedImage,
      width: currentItem.prompt.selectedImageWidth ?? 0,
      height: currentItem.prompt.selectedImageHeight ?? 0,
    });
    if (result.error || !result.uri) {
      updateItemRef.current(item.id, (latest, prompt) => ({
        ...latest,
        status: 'done',
        prompt: { ...prompt, _tempUploadError: true },
      }));
      throw new Error(String(result.error));
    }
    const { _tempSelectedImage, _tempUploadError, ...rest } = previousPrompt;
    const uploadedPrompt: ImagePrompt = {
      ...rest,
      selectedId: result.id ?? rest.selectedId,
      selectedImage: result.uri,
      selectedImagePreview: rest.selectedImagePreview || rest.sourceImagePreview,
    };
    updateItemRef.current(item.id, (latest) => ({
      ...latest,
      status: 'done',
      prompt: uploadedPrompt,
    }));
    return getPromptReplacements(previousPrompt, uploadedPrompt);
  };

  useEffect(() => {
    const retry = async () => {
      const failed = Array.from(refs.current.failed);
      let ok = true;
      let retried = false;
      const replacements: TempUploadReplacement[] = [];
      for (const key of failed) {
        const item = itemsRef.current.find((candidate) => `${keyPrefix}:${candidate.id}` === key);
        if (!item) continue;
        retried = true;
        refs.current.failed.delete(key);
        refs.current.processed.delete(key);
        try {
          replacements.push(...(await runItem(item)));
          refs.current.processed.add(key);
        } catch {
          ok = false;
          refs.current.failed.add(key);
          refs.current.processed.add(key);
          toast({ type: 'error', message: t('popup.toast.uploadingError') });
        }
      }
      return { success: ok, replacements, retried };
    };
    return registerTempUploadRetryHandler(retry);
  }, [keyPrefix, t]);

  useEffect(() => {
    if (!userInfo) return;
    items.forEach((item) => {
      if (item.status !== 'done' || !item.prompt) return;
      if (!extractTempImageData(item.prompt) && !item.prompt._tempSelectedImage) return;
      const key = `${keyPrefix}:${item.id}`;
      if (refs.current.processed.has(key) || refs.current.pending.has(key)) return;
      refs.current.pending.add(key);
      runItem(item)
        .then(() => refs.current.processed.add(key))
        .catch((error) => {
          refs.current.failed.add(key);
          refs.current.processed.add(key);
          toast({ type: 'error', message: t('popup.toast.uploadingError') });
          console.error(`Temp upload failed: ${key}`, error);
        })
        .finally(() => refs.current.pending.delete(key));
    });
  }, [items, keyPrefix, t, userInfo]);
};

export const useRunTempImageUploadBatch = (
  imagePrompts: Array<{
    id: string;
    prompt: ImagePrompt | null;
    status: 'done' | 'uploading' | 'error' | 'removed';
  } | null>,
  _handleImageChange: (idx: number, file: ImageUploadFile) => void,
) => {
  const store = useStoreApi();
  const { userInfo, updateFormParams } = useStore((state) => ({
    userInfo: state.userInfo,
    updateFormParams: state.updateFormParams,
  }));
  useRunTempUploadBatch({
    userInfo,
    items: imagePrompts.filter(Boolean) as UploadItem[],
    keyPrefix: 'img2video',
    getLatestItem: (id) =>
      store.getState().img2videoFormParams.imagePrompts.find((item) => item?.id === id) as UploadItem | undefined,
    updateItem: (id, updater) =>
      updateFormParams(
        'img2video',
        'imagePrompts',
        store
          .getState()
          .img2videoFormParams.imagePrompts.map((item) =>
            item?.id === id && item.prompt ? updater(item, item.prompt) : item,
          ),
      ),
  });
};

export const useRunTempReferenceImageUploadBatch = (
  referencePrompts: ReferencePrompt[],
  updateReferenceAndMentions: (id: string, updates: Partial<ReferencePrompt>) => void,
) => {
  const { userInfo } = useStore((state) => ({ userInfo: state.userInfo }));
  useRunTempUploadBatch({
    userInfo,
    items: referencePrompts.filter(
      (item): item is Extract<ReferencePrompt, { type: 'image' }> => item.type === 'image',
    ) as UploadItem[],
    keyPrefix: 'reference',
    getLatestItem: (id) => referencePrompts.find((item) => item.id === id && item.type === 'image') as UploadItem,
    updateItem: (id, updater) => {
      const item = referencePrompts.find((candidate) => candidate.id === id);
      if (item?.type === 'image' && item.prompt) {
        const next = updater(item, item.prompt);
        updateReferenceAndMentions(id, { prompt: next.prompt, status: next.status });
      }
    },
  });
};
